import React from "react";

import moment, { Moment } from "moment";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider, renderTimeViewClock } from "@mui/x-date-pickers";
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DesktopTimePicker } from '@mui/x-date-pickers/DesktopTimePicker';
import { useAppSelector } from "../../redux";

export const CustomDatePicker: React.FC<{
  label?: string
  fieldName?: string;
  value: string;
  sx?: any;
  disabled?: boolean;
  views?: ("day" | "month" | "year")[];
  inputFormat?: string;
  fullWidth?: boolean;
  onChange: (value: any, formattedDate: string) => void;
  error?: string;
}> = (props) => {
  const {
    fieldName,
    value,
    sx,
    views = ["year", "month", "day"],
    inputFormat = "DD-MM-YYYY",
    fullWidth,
    disabled,
    error,
    label
  } = props;
  
  const customizer = useAppSelector((state) => state.customizer);
  const isDarkMode = customizer.activeMode === "dark";

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <DatePicker
        label={label}
        name={fieldName}
        views={views}
        key={value}
        disabled={disabled}
        format={inputFormat}
        sx={{
          width: fullWidth ? "100%" : "unset",
          "& .MuiInputLabel-root": {
            color: isDarkMode ? '#fff' : '#000',
            "&.Mui-focused": {
              color: isDarkMode ? '#fff' : '#000',
            },
            "&.Mui-error": {
              color: '#d32f2f',
            },
            "&.Mui-disabled": {
              color: isDarkMode ? 'rgba(255, 255, 255, 0.38)' : 'rgba(0, 0, 0, 0.38)',
            },
          },
          "& .MuiOutlinedInput-root": {
            backgroundColor: isDarkMode ? '#1e1e1e' : '#fff',
            color: isDarkMode ? '#fff' : '#000',
            '& fieldset': {
              borderColor: isDarkMode ? '#fff' : '#000',
            },
            '&:hover fieldset': {
              borderColor: isDarkMode ? '#fff' : '#000',
            },
            '&.Mui-focused fieldset': {
              borderColor: isDarkMode ? '#fff' : '#000',
            },
            "&.Mui-error fieldset": {
              borderColor: '#d32f2f',
            },
          },
          "& .MuiOutlinedInput-input": {
            color: isDarkMode ? '#fff' : '#000',
            "&.Mui-disabled": {
              backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.12)' : '#f8f9fb',
              color: isDarkMode ? 'rgba(255, 255, 255, 0.38)' : 'rgba(0, 0, 0, 0.38)',
              "-webkit-text-fill-color": isDarkMode ? 'rgba(255, 255, 255, 0.38)' : 'rgba(0, 0, 0, 0.38)',
            },
          },
          "& .MuiIconButton-root": {
            color: isDarkMode ? '#fff' : '#000',
            "&.Mui-disabled": {
              color: isDarkMode ? 'rgba(255, 255, 255, 0.38)' : 'rgba(0, 0, 0, 0.38)',
            },
          },
          "& .MuiFormHelperText-root": {
            color: isDarkMode ? '#fff' : '#000',
            "&.Mui-error": {
              color: '#d32f2f',
            },
          },
          ...sx
        }}
        value={(moment(value) && value.length > 0 ? moment(value) : null) as any}
        onChange={(newValue) => {
          const formattedDate = moment(newValue).format("YYYY-MM-DD");
          props.onChange(newValue, formattedDate);
        }}
        slotProps={{
          textField: {
            size: "small",
            error: !!error,
            helperText: error,
          },
        }}
      />
    </LocalizationProvider>
  );
};

export const OutlinedTimePicker: React.FC<{
  label: string;
  value: Moment;
  sx?: any;
  fullWidth?: boolean;
  onChange: (value: Moment) => void;
  error?: string;
  format?: string
}> = (props) => {
  const { value, sx, error, label, fullWidth, format = "HH:mm:ss" } = props;
  
  const customizer = useAppSelector((state) => state.customizer);
  const isDarkMode = customizer.activeMode === "dark";

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <TimePicker
        label={label}
        value={moment(value) as any}
        viewRenderers={{
          hours: renderTimeViewClock,
          minutes: renderTimeViewClock,
          seconds: renderTimeViewClock,
        }}
        format={format}
        onChange={(newValue) => {
          if (newValue) {
            props.onChange(newValue);
          }
        }}
        sx={{
          width: fullWidth ? "100%" : "unset",
          "& .MuiInputLabel-root": {
            color: isDarkMode ? '#fff' : '#000',
            "&.Mui-focused": {
              color: isDarkMode ? '#fff' : '#000',
            },
            "&.Mui-error": {
              color: '#d32f2f',
            },
            "&.Mui-disabled": {
              color: isDarkMode ? 'rgba(255, 255, 255, 0.38)' : 'rgba(0, 0, 0, 0.38)',
            },
          },
          "& .MuiOutlinedInput-root": {
            backgroundColor: isDarkMode ? '#1e1e1e' : '#fff',
            color: isDarkMode ? '#fff' : '#000',
            '& fieldset': {
              borderColor: isDarkMode ? '#fff' : '#000',
            },
            '&:hover fieldset': {
              borderColor: isDarkMode ? '#fff' : '#000',
            },
            '&.Mui-focused fieldset': {
              borderColor: isDarkMode ? '#fff' : '#000',
            },
            "&.Mui-error fieldset": {
              borderColor: '#d32f2f',
            },
          },
          "& .MuiOutlinedInput-input": {
            color: isDarkMode ? '#fff' : '#000',
            "&.Mui-disabled": {
              backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.12)' : '#f8f9fb',
              color: isDarkMode ? 'rgba(255, 255, 255, 0.38)' : 'rgba(0, 0, 0, 0.38)',
              "-webkit-text-fill-color": isDarkMode ? 'rgba(255, 255, 255, 0.38)' : 'rgba(0, 0, 0, 0.38)',
            },
          },
          "& .MuiIconButton-root": {
            color: isDarkMode ? '#fff' : '#000',
            "&.Mui-disabled": {
              color: isDarkMode ? 'rgba(255, 255, 255, 0.38)' : 'rgba(0, 0, 0, 0.38)',
            },
          },
          "& .MuiFormHelperText-root": {
            color: isDarkMode ? '#fff' : '#000',
            "&.Mui-error": {
              color: '#d32f2f',
            },
          },
          ...sx
        }}
        slotProps={{
          textField: {
            size: "small",
            error: !!error,
            helperText: error,
          },
        }}
      />
    </LocalizationProvider>
  );
};

export const OutlinedDesktopTimePicker: React.FC<{
  label: string;
  value: Moment;
  sx?: any;
  fullWidth?: boolean;
  onChange: (value: Moment) => void;
  error?: string;
  format?: string
  startTime?: string
}> = (props) => {
  const { value, sx, error, label, fullWidth, format = "HH:mm:ss", startTime } = props;
  
  const customizer = useAppSelector((state) => state.customizer);
  const isDarkMode = customizer.activeMode === "dark";

  let customMinTime = undefined;
  if (startTime && moment(startTime).isValid()) {
    customMinTime = moment(startTime).startOf("hours").endOf("minutes");
  }

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <DesktopTimePicker
        label={label}
        value={moment(value) as any}
        format={format}
        views={['hours', 'minutes']}
        minTime={customMinTime}
        onChange={(newValue) => {
          if (newValue) {
            props.onChange(newValue);
          }
        }}
        sx={{
          width: fullWidth ? "100%" : "unset",
          "& .MuiInputLabel-root": {
            color: isDarkMode ? '#fff' : '#000',
            "&.Mui-focused": {
              color: isDarkMode ? '#fff' : '#000',
            },
            "&.Mui-error": {
              color: '#d32f2f',
            },
            "&.Mui-disabled": {
              color: isDarkMode ? 'rgba(255, 255, 255, 0.38)' : 'rgba(0, 0, 0, 0.38)',
            },
          },
          "& .MuiOutlinedInput-root": {
            backgroundColor: isDarkMode ? '#1e1e1e' : '#fff',
            color: isDarkMode ? '#fff' : '#000',
            '& fieldset': {
              borderColor: isDarkMode ? '#fff' : '#000',
            },
            '&:hover fieldset': {
              borderColor: isDarkMode ? '#fff' : '#000',
            },
            '&.Mui-focused fieldset': {
              borderColor: isDarkMode ? '#fff' : '#000',
            },
            "&.Mui-error fieldset": {
              borderColor: '#d32f2f',
            },
          },
          "& .MuiOutlinedInput-input": {
            color: isDarkMode ? '#fff' : '#000',
            "&.Mui-disabled": {
              backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.12)' : '#f8f9fb',
              color: isDarkMode ? 'rgba(255, 255, 255, 0.38)' : 'rgba(0, 0, 0, 0.38)',
              "-webkit-text-fill-color": isDarkMode ? 'rgba(255, 255, 255, 0.38)' : 'rgba(0, 0, 0, 0.38)',
            },
          },
          "& .MuiIconButton-root": {
            color: isDarkMode ? '#fff' : '#000',
            "&.Mui-disabled": {
              color: isDarkMode ? 'rgba(255, 255, 255, 0.38)' : 'rgba(0, 0, 0, 0.38)',
            },
          },
          "& .MuiFormHelperText-root": {
            color: isDarkMode ? '#fff' : '#000',
            "&.Mui-error": {
              color: '#d32f2f',
            },
          },
          ...sx
        }}
        slotProps={{
          textField: {
            size: "small",
            error: !!error,
            helperText: error,
          },
        }}
      />
    </LocalizationProvider>
  );
};